import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Os ícones vão desenhados aqui dentro, e não vindos de uma biblioteca, porque este
 * pacote é compartilhado pelos dois aplicativos e não carrega dependência de ícones —
 * o resto dele segue a mesma regra.
 */
function Icone({ tamanho = 18, children }) {
    return (_jsx("svg", { width: tamanho, height: tamanho, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: children }));
}
const MICROFONE = (_jsxs(_Fragment, { children: [_jsx("path", { d: "M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" }), _jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), _jsx("path", { d: "M12 19v3" })] }));
const LIXEIRA = (_jsxs(_Fragment, { children: [_jsx("path", { d: "M3 6h18" }), _jsx("path", { d: "M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" }), _jsx("path", { d: "M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" }), _jsx("path", { d: "M10 11v6M14 11v6" })] }));
const PAUSA = (_jsxs(_Fragment, { children: [_jsx("rect", { x: "6", y: "4", width: "4", height: "16", rx: "1" }), _jsx("rect", { x: "14", y: "4", width: "4", height: "16", rx: "1" })] }));
const CONTINUAR = _jsx("path", { d: "M6 3l14 9-14 9V3Z" });
/**
 * Formatos que o MediaRecorder pode entregar, em ordem de preferência.
 *
 * `audio/mp4` (AAC) primeiro porque toca em tudo, inclusive no Safari do iPhone. O
 * `webm/opus` é o que o Chrome sempre soube gravar, mas o iOS não reproduz: um áudio que
 * o atendente grava no PC chegaria mudo para o cliente no celular. Chrome recente já
 * grava mp4, então a negociação resolve a maioria dos casos sem conversão no servidor.
 *
 * O caso que sobra é o navegador que só oferece webm — Firefox, por exemplo. Esse áudio
 * sobe como está e o servidor o converte para m4a em segundo plano (ffmpeg, fila `midia`):
 * a bolha aparece na hora e o arquivo é trocado embaixo dela um segundo depois, sem que
 * quem gravou perceba. Por isso a negociação aqui continua valendo a pena: o que já nasce
 * tocável não passa por conversão nenhuma.
 */
const FORMATOS = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];
/** Cinco minutos. Acima disso não é recado, é reunião. */
const LIMITE_SEGUNDOS = 300;
/**
 * Quantas medidas de volume ficam guardadas. Quantas aparecem depende da largura da tela
 * (ver `BarraGravacao`) — este é o teto, dimensionado para a barra mais larga possível.
 */
const BARRAS = 140;
/** Largura de cada barrinha da onda, com o vão: 2px de traço e 1px de respiro. */
const PASSO_BARRA = 3;
/** De quanto em quanto tempo se mede o volume. 12 medidas por segundo já parece vivo. */
const INTERVALO_AMOSTRA_MS = 80;
function escolherFormato() {
    if (typeof MediaRecorder === 'undefined')
        return null;
    return FORMATOS.find((f) => MediaRecorder.isTypeSupported(f)) ?? null;
}
/**
 * O estado de uma gravação de áudio, para a barra de escrever virar gravador.
 *
 * É um hook, e não um componente fechado, porque o desenho pedido (28/09) é o do
 * WhatsApp: enquanto grava, a onda ocupa o lugar do campo de texto e quem envia é o mesmo
 * botão de sempre. Isso só é possível se quem monta a barra tiver o controle — antes o
 * gravador era uma ilha com os seus próprios botões de "Enviar" e "Descartar", e era
 * exatamente disso que a tela não precisava.
 */
export function useGravadorAudio({ onGravado, onErro }) {
    const [gravando, setGravando] = useState(false);
    const [pausado, setPausado] = useState(false);
    const [segundos, setSegundos] = useState(0);
    const [niveis, setNiveis] = useState([]);
    const recorder = useRef(null);
    const pedacos = useRef([]);
    // Um envio cancelado não pode virar mensagem: o `stop` dispara o mesmo evento nos dois
    // casos, então é preciso lembrar o que a pessoa pediu.
    const descartando = useRef(false);
    const audioCtx = useRef(null);
    const analisador = useRef(null);
    const relogio = useRef(null);
    const amostragem = useRef(null);
    /** Para os dois relógios (o do tempo e o da onda) sem desmontar o microfone. */
    const pararIntervalos = useCallback(() => {
        if (relogio.current !== null)
            clearInterval(relogio.current);
        if (amostragem.current !== null)
            clearInterval(amostragem.current);
        relogio.current = null;
        amostragem.current = null;
    }, []);
    const encerrarMedicao = useCallback(() => {
        pararIntervalos();
        analisador.current = null;
        void audioCtx.current?.close().catch(() => undefined);
        audioCtx.current = null;
    }, [pararIntervalos]);
    /** Liga os dois relógios: o do tempo decorrido e o que alimenta a onda. */
    const ligarIntervalos = useCallback(() => {
        if (relogio.current === null) {
            relogio.current = window.setInterval(() => {
                setSegundos((s) => {
                    // O limite fecha a gravação e envia o que já existe — jogar fora cinco minutos
                    // de fala por causa do teto seria pior do que o teto.
                    if (s + 1 >= LIMITE_SEGUNDOS)
                        pararRef.current?.(false);
                    return s + 1;
                });
            }, 1000);
        }
        if (amostragem.current === null && analisador.current) {
            const amostra = new Uint8Array(analisador.current.fftSize);
            amostragem.current = window.setInterval(() => {
                const atual = analisador.current;
                if (!atual)
                    return;
                atual.getByteTimeDomainData(amostra);
                // Volume eficaz (RMS) da janela: 128 é o silêncio no formato de 8 bits.
                let soma = 0;
                for (const v of amostra)
                    soma += ((v - 128) / 128) ** 2;
                // O multiplicador deixa a fala normal ocupar boa parte da altura; sem ele a onda
                // fica quase reta e não parece que está gravando.
                const nivel = Math.min(1, Math.sqrt(soma / amostra.length) * 3.2);
                setNiveis((atuais) => [...atuais, nivel].slice(-BARRAS));
            }, INTERVALO_AMOSTRA_MS);
        }
    }, []);
    const parar = useCallback((descartar) => {
        descartando.current = descartar;
        encerrarMedicao();
        recorder.current?.stop();
        recorder.current = null;
        setGravando(false);
        setPausado(false);
        setNiveis([]);
    }, [encerrarMedicao]);
    // `ligarIntervalos` precisa chamar `parar` quando o tempo estoura, e `parar` é declarado
    // depois: a referência resolve a ordem sem criar dependência circular entre os dois.
    const pararRef = useRef(parar);
    pararRef.current = parar;
    // Soltar o microfone ao sair da tela, senão o indicador do navegador fica aceso e a
    // aba continua "ouvindo" depois que ninguém está mais olhando.
    useEffect(() => {
        return () => {
            encerrarMedicao();
            recorder.current?.stream.getTracks().forEach((t) => t.stop());
        };
    }, [encerrarMedicao]);
    const comecar = useCallback(async () => {
        const formato = escolherFormato();
        if (!formato) {
            onErro?.('Este navegador não grava áudio. Anexe um arquivo de áudio.');
            return;
        }
        try {
            const fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
            const gravador = new MediaRecorder(fluxo, { mimeType: formato });
            pedacos.current = [];
            descartando.current = false;
            gravador.ondataavailable = (e) => {
                if (e.data.size)
                    pedacos.current.push(e.data);
            };
            gravador.onstop = () => {
                fluxo.getTracks().forEach((t) => t.stop());
                const audio = new Blob(pedacos.current, { type: formato });
                if (!descartando.current && audio.size > 0) {
                    onGravado(audio, formato.split(';')[0] ?? formato);
                }
                pedacos.current = [];
            };
            gravador.start();
            recorder.current = gravador;
            // A onda é só leitura do microfone, em paralelo com a gravação: o analisador não
            // entra no caminho do áudio, então não há risco de mexer no que vai ser enviado.
            try {
                const contexto = new AudioContext();
                const no = contexto.createAnalyser();
                no.fftSize = 512;
                contexto.createMediaStreamSource(fluxo).connect(no);
                audioCtx.current = contexto;
                analisador.current = no;
            }
            catch {
                // Sem AudioContext a gravação continua; só a onda fica parada. Não é motivo
                // para impedir alguém de mandar um áudio.
            }
            setSegundos(0);
            setNiveis([]);
            setPausado(false);
            setGravando(true);
            ligarIntervalos();
        }
        catch {
            // Negar o microfone é escolha legítima; não vale insistir nem explicar demais.
            onErro?.('Não foi possível usar o microfone. Verifique a permissão do navegador.');
        }
    }, [onErro, onGravado, ligarIntervalos]);
    const alternarPausa = useCallback(() => {
        const gravador = recorder.current;
        if (!gravador)
            return;
        if (gravador.state === 'recording') {
            gravador.pause();
            setPausado(true);
        }
        else if (gravador.state === 'paused') {
            gravador.resume();
            setPausado(false);
        }
    }, []);
    // Em pausa o relógio para e a onda congela: é o que a pessoa espera ao pausar, e
    // continuar contando daria um áudio com duração maior do que o som dentro dele.
    useEffect(() => {
        if (!gravando)
            return;
        if (pausado)
            pararIntervalos();
        else
            ligarIntervalos();
    }, [gravando, pausado, pararIntervalos, ligarIntervalos]);
    return {
        gravando,
        pausado,
        segundos,
        niveis,
        comecar,
        alternarPausa,
        descartar: () => parar(true),
        finalizar: () => parar(false),
    };
}
/** O microfone na barra de escrever. Vira gravação ao ser tocado. */
export function BotaoMicrofone({ gravador, desabilitado, tamanho = 18, }) {
    return (_jsx("button", { type: "button", onClick: () => void gravador.comecar(), disabled: desabilitado, title: "Gravar \u00E1udio", "aria-label": "Gravar \u00E1udio", className: "shrink-0 rounded-full p-2 text-neutral-500 hover:bg-black/5 disabled:opacity-40 dark:hover:bg-white/10", children: _jsx(Icone, { tamanho: tamanho, children: MICROFONE }) }));
}
/**
 * A barra que toma o lugar do campo de texto enquanto grava: lixeira, onda, relógio e
 * pausa. Quem envia é o botão de enviar da própria tela, como no WhatsApp — por isso não
 * há botão de enviar aqui dentro.
 */
export function BarraGravacao({ gravador }) {
    const { niveis, pausado, segundos } = gravador;
    const minutos = String(Math.floor(segundos / 60)).padStart(2, '0');
    const resto = String(segundos % 60).padStart(2, '0');
    // Quantas barras cabem de verdade. Sem medir, um número fixo espreme a onda numa coluna
    // estreita (a do atendente, ao lado do painel do cliente) e a deixa rala numa tela larga.
    const area = useRef(null);
    const [cabem, setCabem] = useState(40);
    useEffect(() => {
        const alvo = area.current;
        if (!alvo)
            return;
        const medir = () => setCabem(Math.max(8, Math.min(BARRAS, Math.floor(alvo.clientWidth / PASSO_BARRA))));
        medir();
        const observador = new ResizeObserver(medir);
        observador.observe(alvo);
        return () => observador.disconnect();
    }, []);
    // A onda corre da direita para a esquerda: o que passa do que cabe sai de cena, e as
    // barras que ainda faltam entram como silêncio à esquerda, para o desenho não "pular"
    // de tamanho nos primeiros segundos.
    const recentes = niveis.slice(-cabem);
    const desenho = [...Array(Math.max(0, cabem - recentes.length)).fill(0), ...recentes];
    return (_jsxs("div", { className: "flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-neutral-300 bg-white px-2 py-1.5 dark:border-neutral-700 dark:bg-neutral-900", children: [_jsx("button", { type: "button", onClick: gravador.descartar, title: "Descartar o \u00E1udio", "aria-label": "Descartar o \u00E1udio", className: "shrink-0 rounded-full p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50", children: _jsx(Icone, { children: LIXEIRA }) }), !pausado && (_jsx("span", { className: "size-2 shrink-0 animate-pulse rounded-full bg-red-500", "aria-hidden": "true" })), _jsx("div", { ref: area, className: "flex h-8 min-w-0 flex-1 items-center justify-end gap-px overflow-hidden", "aria-hidden": "true", children: desenho.map((nivel, i) => (_jsx("span", { className: `w-0.5 shrink-0 rounded-full ${pausado ? 'bg-neutral-400' : 'bg-marca-500'}`, 
                    // Mínimo de 2px para a linha existir no silêncio, como no WhatsApp.
                    style: { height: `${Math.max(2, nivel * 28)}px` } }, i))) }), _jsxs("span", { className: "shrink-0 tabular-nums text-sm", "aria-live": "polite", children: [minutos, ":", resto] }), _jsx("button", { type: "button", onClick: gravador.alternarPausa, title: pausado ? 'Continuar gravando' : 'Pausar', "aria-label": pausado ? 'Continuar gravando' : 'Pausar', className: "shrink-0 rounded-full p-1.5 text-neutral-600 hover:bg-black/5 dark:text-neutral-300 dark:hover:bg-white/10", children: _jsx(Icone, { children: pausado ? CONTINUAR : PAUSA }) })] }));
}
//# sourceMappingURL=GravadorAudio.js.map